import React from "react";
import { SimilarProductRecommendationCardProps } from "./SimilarProductRecommendationCard.types";
import styled from "@emotion/styled";
import Box from "../../../primitives/Box/Box";
import FlexColumn from "../../../components/Layout/FlexColumn/FlexColumn";
import { StyledImage } from "../../../components/Image/Image";
import FlexRow from "../../../components/Layout/FlexRow/FlexRow";
import {
  Body5RegularTypography,
  Body3MediumTypography,
  Body6MediumTypography,
  Caps2SemiboldTypography,
} from "../../../primitives/Typography/typographyVariants";
import { ActionButton } from "../../../components/ActionButton/ActionButton";
import { Icon } from "../../../components/Icon/Icon";

export const DiscountBadge = styled(Box)`
  background: linear-gradient(270deg, #efa396 0%, #fff 100%);
`;

const calculatePercentageOff = (
  price: number | undefined,
  mrp: number | undefined
): number => {
  if (!mrp || mrp === 0 || !price) return 0;
  const percentageOff = ((mrp - price) / mrp) * 100;
  return Math.round(percentageOff);
};

export const SimilarProductRecommendationCard: React.FC<
  SimilarProductRecommendationCardProps
> = ({
  data,
  index = 0,
  onCtaClick,
  onCardClick,
  className,
  dataCy,
  placeholderImageUrl,
  ctaText = "Share Wishlink",
  showDiscountBadge = true,
}) => {
  const product = data?.product || {};

  const handleGetWishlink = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    if (onCtaClick) {
      onCtaClick();
    }
  };

  const handleProductClick = () => {
    if (onCardClick) {
      onCardClick();
    }
  };

  const percentageOff = calculatePercentageOff(product?.price, product?.mrp);
  const roundedMrp = Math.round(product?.mrp || 0);
  const roundedPrice = Math.round(product?.price || 0);
  const showMrp = roundedMrp > 0 && roundedMrp !== roundedPrice;

  return (
    <FlexColumn
      alignItems="center"
      justifyContent="center"
      width="50%"
      position="relative"
      id={dataCy || `growth-follower-love-product-card-${index}`}
      className={className || `overlay-similar-product-${index}`}
      onClick={handleProductClick}
      borderRadius={8}
      border="0.5px solid"
      borderColor="border.default.default"
    >
      <StyledImage
        src={product?.thumbnail_url || ""}
        placeholder={placeholderImageUrl}
        alt={product?.name || "product"}
        width="100%"
        aspectRatio="1/1"
        objectFit="cover"
      />
      <FlexColumn p={8} gap={8}>
        <FlexColumn gap={2}>
          <Caps2SemiboldTypography
            fontFamily="Lexend"
            color="text.default.secondary"
            textTransform="uppercase"
            lineClamp={1}
            textOverflow="ellipsis"
            maxWidth="100%"
          >
            {product?.brand || ""}
          </Caps2SemiboldTypography>
          <Body5RegularTypography
            fontFamily="Lexend"
            color="text.default.default"
            lineClamp={1}
            textOverflow="ellipsis"
            maxWidth="100%"
          >
            {product?.name || ""}
          </Body5RegularTypography>
          <FlexRow gap={4}>
            {showMrp && (
              <Body5RegularTypography
                fontFamily="Lexend"
                color="text.default.secondary"
                textDecoration="line-through"
              >
                ₹{roundedMrp}
              </Body5RegularTypography>
            )}
            <Body3MediumTypography
              fontFamily="Lexend"
              color="text.default.default"
            >
              ₹{roundedPrice}
            </Body3MediumTypography>
            {showDiscountBadge && percentageOff > 0 && (
              <DiscountBadge py={2} px={4}>
                <Body6MediumTypography
                  fontFamily="Lexend"
                  color="text.wishlink.default"
                >
                  {percentageOff}% OFF
                </Body6MediumTypography>
              </DiscountBadge>
            )}
          </FlexRow>
        </FlexColumn>
        <ActionButton
          variant="primary"
          outlined={true}
          onClick={handleGetWishlink}
          width="100%"
          leftIcon={
            <Icon
              name={
                ctaText === "Share Wishlink"
                  ? "fa-solid fa-share"
                  : "fa-sharp fa-solid fa-plus fa-xs"
              }
              size="14px"
              height="18px"
              width="9px"
              color="inherit"
            />
          }
        >
          {ctaText}
        </ActionButton>
      </FlexColumn>
    </FlexColumn>
  );
};
